import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export type DashboardSupportCategory =
  | "request_support"
  | "provide_feedback"
  | "feature_request"
  | "other";

const CATEGORY_OPTIONS: { id: DashboardSupportCategory; label: string }[] = [
  { id: "request_support", label: "Request Support" },
  { id: "provide_feedback", label: "Provide feedback" },
  { id: "feature_request", label: "Feature request" },
  { id: "other", label: "Other" },
];

export interface DashboardSupportFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  portalDocument: Document;
  /**
   * Record feedback via analytics only (no support email API), same idea as
   * gsheets-addon ExperienceFeedback `track("agent_action_completed", ...)`.
   */
  onSubmit: (category: DashboardSupportCategory, message: string) => void;
  onValidationError: (message: string) => void;
}

export const DashboardSupportFeedbackModal: React.FC<DashboardSupportFeedbackModalProps> = ({
  open,
  onClose,
  portalDocument,
  onSubmit,
  onValidationError,
}) => {
  const [category, setCategory] = useState<DashboardSupportCategory>("request_support");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    portalDocument.defaultView?.addEventListener("keydown", onKey);
    return () => portalDocument.defaultView?.removeEventListener("keydown", onKey);
  }, [open, onClose, portalDocument]);

  useEffect(() => {
    if (open) {
      setCategory("request_support");
      setMessage("");
    }
  }, [open]);

  const handleSend = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed) {
      onValidationError("Please enter a message.");
      return;
    }
    onSubmit(category, trimmed);
    onClose();
  }, [category, message, onClose, onSubmit, onValidationError]);

  if (!open) {
    return null;
  }

  const root = portalDocument.body;
  if (!root) {
    return null;
  }

  const modal = (
    <div
      className="fixed inset-0 z-[280] flex items-center justify-center bg-black/40 px-4 py-8"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative flex w-[min(500px,calc(100vw-2rem))] max-h-[min(80vh,calc(100vh-4rem))] flex-col overflow-hidden rounded-2xl bg-main-background shadow-[0_8px_40px_rgba(0,0,0,0.2)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dash-support-feedback-title"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — aligned with Share dashboard dialog */}
        <div className="flex flex-shrink-0 items-center justify-between px-6 pt-5 pb-2">
          <h2
            id="dash-support-feedback-title"
            className="text-xl font-semibold tracking-tight text-main-foreground"
          >
            Support &amp; Feedback
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-full p-1.5 text-main-muted-foreground transition-colors hover:bg-main-text-button-hover-background hover:text-main-foreground"
            aria-label="Close"
          >
            <span className="material-icons !text-xl">close</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <div className="my-5 flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map(opt => {
              const active = opt.id === category;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setCategory(opt.id)}
                  className={
                    "rounded-full px-4 py-2 text-sm font-medium leading-snug transition-colors " +
                    (active
                      ? "bg-primary-button-background/15 text-primary-button-background ring-2 ring-primary-button-background/30"
                      : "bg-main-text-button-hover-background text-main-foreground hover:bg-main-text-button-hover-background/80")
                  }
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <textarea
            id="dash-support-feedback-message"
            rows={6}
            placeholder="Describe your question or feedback…"
            value={message}
            onChange={e => setMessage(e.target.value)}
            className="min-h-[160px] w-full resize-y rounded-lg border border-main-border bg-main-background p-4 text-base leading-relaxed text-main-foreground placeholder:text-main-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-button-background"
            aria-label="Message"
          />

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleSend}
              className="h-[40px] min-w-[140px] cursor-pointer rounded-lg bg-primary-button-background px-6 text-sm font-medium text-primary-button-foreground transition-colors hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, root);
};
